/**
 * Alle legalen Aktionen eines Spielers — mit **Action IDs**.
 *
 * Dies ist die Schnittstelle, über die ein Zug entsteht: Bewegung, Schlag, Push und
 * Respawn in **einer** Liste, jede Aktion mit einer eindeutigen, deterministischen ID.
 *
 * **Warum IDs und nicht Koordinaten** (§74, `docs/ARCHITECTURE.md` E10): Der Client — und
 * ab V2 ein LLM — wählt eine ID aus einer Liste, die der Server selbst berechnet hat. Der
 * Server validiert dann nicht Koordinaten gegen Regeln, sondern prüft nur, ob die ID in
 * seiner eigenen Liste steht. Damit kann kein Client und kein Sprachmodell eine Aktion
 * erzwingen, die die Rule Engine nicht angeboten hat (§108, §110). Das ist eine der vier
 * KI-Nahtstellen, die in V1 gebaut werden, obwohl V1 noch keine KI hat — nachträglich
 * wäre es ein Umbau jedes Aufrufpfads.
 *
 * **Form der ID:** `TYP:figurId:feldId`, mit den kanonischen Feld-IDs der Referenzgrafik
 * (`MOVE:blue-guard-1:green-A1`). Bewusst lesbar statt opak: sie taucht in der
 * Debug-Ansicht, im Event-Log und ab V2 im KI-Kontext auf, und dort ist eine ID, die man
 * ohne Nachschlagen versteht, ein echter Vorteil. Die Eindeutigkeit garantiert, dass
 * Figur und Zielfeld eine Aktion vollständig bestimmen.
 *
 * Die IDs sind **deterministisch** — zweimal dieselbe Stellung ergibt zweimal dieselben
 * IDs. Das ist Voraussetzung für reproduzierbare Partien (§128) und dafür, dass eine
 * KI-Antwort auf die Liste passt, die ihr vorgelegt wurde.
 */
import { getLegalCaptures } from './legal-captures.js';
import { getLegalMoves } from './legal-moves.js';
import { getLegalPushes } from './legal-pushes.js';
import { getLegalRespawns } from './legal-respawns.js';
import { cellId } from './sectors.js';
import { pieceAt, piecesOf, requirePiece } from './board-state.js';
function actionId(context, type, piece, cell) {
    return `${type}:${piece}:${cellId(context.layout, cell)}`;
}
/**
 * Alle Aktionen, die dieser Spieler in diesem Zustand ausführen darf.
 *
 * Die Reihenfolge ist stabil: je Figur zuerst Bewegungen, dann Schläge, dann Stöße — in
 * der Reihenfolge, in der die Figuren im Zustand liegen —, danach die Respawns. Ein Zug
 * besteht aus **genau einer** dieser Aktionen (§12).
 */
export function getLegalActions(context, player) {
    const actions = [];
    for (const piece of piecesOf(context.state, player)) {
        for (const to of getLegalMoves(context, piece.id)) {
            actions.push({
                id: actionId(context, 'MOVE', piece.id, to),
                type: 'MOVE',
                player,
                piece: piece.id,
                from: piece.at,
                to,
            });
        }
        for (const to of getLegalCaptures(context, piece.id)) {
            const victim = pieceAt(context.state, to);
            if (victim === undefined)
                continue;
            actions.push({
                id: actionId(context, 'CAPTURE', piece.id, to),
                type: 'CAPTURE',
                player,
                piece: piece.id,
                from: piece.at,
                to,
                victim: victim.id,
            });
        }
        for (const push of getLegalPushes(context, piece.id)) {
            actions.push({
                id: actionId(context, 'PUSH', piece.id, push.victimAt),
                type: 'PUSH',
                player,
                piece: piece.id,
                from: piece.at,
                to: push.victimAt,
                victim: push.victim,
                victimAt: push.victimAt,
                victimTo: push.victimTo,
                capturedByPush: push.capturedByPush,
                pusherTo: push.pusherTo,
            });
        }
    }
    for (const respawn of getLegalRespawns(context, player)) {
        actions.push({
            id: actionId(context, 'RESPAWN', respawn.piece, respawn.to),
            type: 'RESPAWN',
            player,
            piece: respawn.piece,
            to: respawn.to,
        });
    }
    return actions;
}
/**
 * Die Aktion zu dieser ID — oder `undefined`.
 *
 * So validiert der Server eine eingehende Absicht: er berechnet die Liste selbst und
 * schlägt die ID darin nach. Eine ID, die einem **anderen** Spieler gehört, findet sich in
 * dieser Liste nicht und ist damit nicht einschleusbar (§110).
 */
export function findLegalAction(context, player, id) {
    return getLegalActions(context, player).find((action) => action.id === id);
}
/** Nur die Aktionen einer einzelnen Figur — für die Auswahl am Brett (§50). */
export function getLegalActionsForPiece(context, pieceId) {
    const piece = requirePiece(context.state, pieceId);
    return getLegalActions(context, piece.owner).filter((action) => action.piece === pieceId);
}
//# sourceMappingURL=legal-actions.js.map